import { Suspense, useMemo } from 'react';
import clsx from 'clsx';
import thumbDown from './assets/thumb-down.json';
import thumbUp from './assets/thumb-up.json';
import { rgbTupleFromHex } from '@/lib/utils/color';
import { DotLottiePlayer } from '@/lib/lottie/dot-lottie-player';
import styles from './reaction-overlay.module.less';

interface ReactionOverlayProps {
  type: 'up' | 'down';
  color: string;
  compact?: boolean;
  onComplete: () => void;
}

export function recolorReaction(animation: Record<string, unknown>, hex: string) {
  const color = rgbTupleFromHex(hex)
    .split(',')
    .map((channel) => Number(channel.trim()) / 255);
  const copy = globalThis.structuredClone(animation);

  const recolor = (value: unknown): void => {
    if (!value || typeof value !== 'object') return;
    const node = value as Record<string, unknown>;
    const colorProperty = node.c as { k?: unknown } | undefined;
    if ((node.ty === 'fl' || node.ty === 'st') && Array.isArray(colorProperty?.k)) {
      if (typeof colorProperty.k[0] === 'number') {
        colorProperty.k.splice(0, 3, ...color);
      } else {
        colorProperty.k.forEach((keyframe) => {
          const startColor = (keyframe as { s?: unknown }).s;
          if (Array.isArray(startColor)) startColor.splice(0, 3, ...color);
        });
      }
    }
    Object.values(node).forEach(recolor);
  };

  recolor(copy);
  return copy;
}

export function ReactionOverlay({ type, color, compact, onComplete }: ReactionOverlayProps) {
  const data = useMemo(
    () => recolorReaction(type === 'up' ? thumbUp : thumbDown, color),
    [color, type]
  );

  return (
    <Suspense fallback={null}>
      <DotLottiePlayer
        className={clsx(styles.reaction, compact && styles.compact)}
        data={data}
        autoplay
        loop={false}
        dotLottieRefCallback={(animation) => {
          animation?.addEventListener('complete', onComplete);
          animation?.addEventListener('loadError', onComplete);
        }}
        aria-hidden="true"
      />
    </Suspense>
  );
}
